import { Crown, Star } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import type { MembershipOffer } from '@/hooks/estimates/useMembershipOfferPreview';
import { membershipOfferPriceIntervalLabel } from '@/lib/sales/membership-offer';

function fmt(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

/**
 * NOT-605 — the "join and save today" upsell block. Shows the document's price
 * side by side with what the customer would pay as a member, plus the offer to
 * add the membership. Benefit-only plans remain actionable even when this
 * document's immediate savings are $0 (see {@link useMembershipOfferPreview}).
 */
export function MembershipOfferBlock({
  offer,
  onAddMembership,
  selected,
  onSelectedChange,
  selectedBillingOptionId,
  onBillingOptionChange,
  variant = 'choice',
  compact = false,
  isAdding = false,
  className,
}: {
  offer: MembershipOffer | null;
  onAddMembership?: (planId: string) => void;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  selectedBillingOptionId?: string | null;
  onBillingOptionChange?: (billingOptionId: string) => void;
  variant?: 'choice' | 'banner';
  compact?: boolean;
  isAdding?: boolean;
  className?: string;
}) {
  if (!offer) return null;

  const intervalLabel = membershipOfferPriceIntervalLabel(offer);
  const visitsLabel =
    offer.visitsPerYear && offer.visitsPerYear > 0
      ? `${offer.visitsPerYear} visit${offer.visitsPerYear === 1 ? '' : 's'} / year`
      : null;
  const customerChoice = typeof onSelectedChange === 'function';
  const hasImmediateSavings = offer.savings > 0;
  const customerBillingChoice =
    typeof onBillingOptionChange === 'function' && (offer.billingOptions?.length ?? 0) > 1;
  const membershipTotal =
    offer.planPrice == null
      ? null
      : Math.round(
          (offer.memberGrandTotal + offer.planPrice + (offer.setupFee ?? 0) + Number.EPSILON) * 100,
        ) / 100;

  if (customerChoice && variant === 'banner') {
    const planPriceCopy =
      offer.planPrice == null
        ? null
        : `${fmt(offer.planPrice)}${intervalLabel}${offer.setupFee ? ` + ${fmt(offer.setupFee)} one-time setup fee` : ''}`;

    return (
      <section
        aria-label={`${offer.planName} membership offer`}
        className={[
          compact
            ? 'rounded-[10px] border border-[#F6A27C] bg-[#FEF1EB] px-4 py-3.5'
            : 'rounded-[12px] border border-[#F6A27C] bg-[#FEF1EB] px-5 py-5 sm:px-8 sm:py-6',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div
          className={
            compact
              ? 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4'
              : 'flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between sm:gap-8'
          }
        >
          <div
            className={
              compact ? 'flex min-w-0 items-start gap-3' : 'flex min-w-0 items-start gap-4'
            }
          >
            <Star
              aria-hidden="true"
              className={`${compact ? 'mt-0.5 size-4' : 'mt-1 size-5'} shrink-0 text-[#C84E1B]`}
              strokeWidth={1.8}
            />
            <div className="min-w-0">
              <h3
                className={
                  compact
                    ? 'text-[15px] font-medium leading-5 tracking-[-0.005em] text-[#17171A]'
                    : 'text-[18px] font-medium leading-6 tracking-[-0.01em] text-[#17171A] sm:text-[20px]'
                }
              >
                {selected
                  ? hasImmediateSavings
                    ? `${offer.planName} added — you're saving ${fmt(offer.savings)} on this job`
                    : `${offer.planName} membership added`
                  : hasImmediateSavings
                    ? `Save ${fmt(offer.savings)} on this job with ${offer.planName}`
                    : `Add ${offer.planName} membership`}
              </h3>
              <p
                className={
                  compact
                    ? 'mt-0.5 text-[12px] leading-[1.45] text-[#63646B]'
                    : 'mt-1 text-[14px] leading-6 text-[#63646B] sm:text-[15px]'
                }
              >
                {selected
                  ? hasImmediateSavings
                    ? `${planPriceCopy ? `${planPriceCopy} membership is` : 'Membership is'} included in your total. Savings apply to the work only, before the membership price and any setup fee.`
                    : `${planPriceCopy ? `${planPriceCopy} membership is` : 'Membership is'} included in your total with its plan benefits.`
                  : hasImmediateSavings
                    ? `${planPriceCopy ? `${planPriceCopy}. ` : ''}Membership discounts apply to this estimate immediately.`
                    : `${planPriceCopy ? `${planPriceCopy}. ` : ''}Join to receive the plan's visits, waivers, and other benefits.`}
              </p>
              {customerBillingChoice ? (
                <div className="mt-3 max-w-xs">
                  <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#63646B]">
                    Billing option
                  </label>
                  <Select
                    value={selectedBillingOptionId ?? offer.billingOptionId ?? ''}
                    onValueChange={onBillingOptionChange}
                  >
                    <SelectTrigger
                      aria-label="Membership billing option"
                      className="h-9 bg-white text-left text-[13px]"
                    >
                      <SelectValue placeholder="Choose billing" />
                    </SelectTrigger>
                    <SelectContent>
                      {(offer.billingOptions ?? []).map((option) => {
                        const choiceInterval = membershipOfferPriceIntervalLabel({
                          planPriceInterval: option.intervalUnit,
                          planPriceIntervalValue: option.intervalValue,
                        });
                        return (
                          <SelectItem key={option.id} value={option.id}>
                            {option.label} · {fmt(option.price)}
                            {choiceInterval}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            aria-pressed={Boolean(selected)}
            disabled={membershipTotal === null}
            onClick={() => onSelectedChange(!selected)}
            className={`${compact ? 'min-h-9 rounded-[7px] px-4 py-2 text-[13px]' : 'min-h-11 rounded-[8px] px-6 py-2.5 text-[15px]'} shrink-0 bg-[#E95F28] font-medium text-white transition-colors hover:bg-[#CF4F1D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#17171A] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {selected ? 'Remove membership' : 'Add membership'}
          </button>
        </div>
      </section>
    );
  }

  return (
    <div
      className={[
        'rounded-md border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-500/40 dark:bg-amber-500/10',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex items-center gap-1.5 text-sm font-medium text-amber-800 dark:text-amber-300">
        <Crown className="h-4 w-4" />
        {hasImmediateSavings
          ? `Save ${fmt(offer.savings)} today with ${offer.planName}`
          : `Join ${offer.planName}`}
      </div>

      {customerChoice ? (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            aria-pressed={!selected}
            onClick={() => onSelectedChange(false)}
            className={`rounded-md border bg-background px-3 py-2 text-left transition-colors ${
              !selected
                ? 'border-foreground ring-1 ring-foreground'
                : 'border-border hover:border-muted-foreground'
            }`}
          >
            <span className="block text-xs font-medium">Approve estimate only</span>
            <span className="mt-0.5 block text-base font-semibold">
              {fmt(offer.nonMemberGrandTotal)}
            </span>
          </button>
          <button
            type="button"
            aria-pressed={Boolean(selected)}
            disabled={membershipTotal === null}
            onClick={() => onSelectedChange(true)}
            className={`rounded-md border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              selected
                ? 'border-amber-700 bg-amber-100 ring-1 ring-amber-700 dark:bg-amber-500/20'
                : 'border-amber-300 bg-background hover:border-amber-600'
            }`}
          >
            <span className="block text-xs font-medium">Join {offer.planName} and approve</span>
            <span className="mt-0.5 block text-base font-semibold text-amber-800 dark:text-amber-200">
              {membershipTotal === null ? 'Price unavailable' : fmt(membershipTotal)}
            </span>
          </button>
        </div>
      ) : hasImmediateSavings ? (
        <div className="mt-2 flex items-end gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {offer.comparesExistingMembership ? 'Current plans' : 'Non-member'}
            </div>
            <div className="text-base font-medium text-muted-foreground line-through">
              {fmt(offer.nonMemberGrandTotal)}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-amber-700 dark:text-amber-300">
              {offer.comparesExistingMembership ? `With ${offer.planName}` : 'As a member'}
            </div>
            <div className="text-lg font-semibold text-amber-800 dark:text-amber-200">
              {fmt(offer.memberGrandTotal)}
            </div>
          </div>
        </div>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">
          Add this membership for its included visits, waivers, and other plan benefits.
        </p>
      )}

      <p className="mt-2 text-xs text-muted-foreground">
        {hasImmediateSavings
          ? offer.planPrice != null
            ? `${offer.comparesExistingMembership ? 'Add' : 'Join'} ${offer.planName} for ${fmt(offer.planPrice)}${intervalLabel} and save ${fmt(offer.savings)} on this estimate.`
            : `${offer.comparesExistingMembership ? 'Add' : 'Join'} ${offer.planName} and save ${fmt(offer.savings)} on this estimate.`
          : offer.planPrice != null
            ? `Join ${offer.planName} for ${fmt(offer.planPrice)}${intervalLabel}.`
            : `Join ${offer.planName}.`}
        {visitsLabel ? ` Includes ${visitsLabel}.` : ''}
      </p>
      {!!offer.setupFee && (
        <p className="mt-1 text-xs text-muted-foreground">
          One-time setup fee: +{fmt(offer.setupFee)}. Separate from the recurring price.
        </p>
      )}

      {onAddMembership && !customerChoice && (
        <Button
          type="button"
          size="sm"
          className="mt-2 bg-amber-600 text-white hover:bg-amber-700"
          disabled={isAdding}
          onClick={() => onAddMembership(offer.planId)}
        >
          {isAdding ? 'Adding…' : 'Add membership'}
        </Button>
      )}
    </div>
  );
}
